import { useTranslation } from 'react-i18next';
import { IControllerAvailableUpdateInfo } from 'lavva.exalushome/build/js/Services/Updates/IUpdateInfo';
import {
  DownloadProgressInfo,
  UpdateProgressInfo,
  UpdateStatus,
} from 'lavva.exalushome/build/js/Services/Updates/IUpdatesService';
import { useBackdropContext } from '../../../../../context/backdrop';
import { langCode } from '../../../utils/updates';

type IUpdateProcess = {
  checkForControllerUpdates: () => Promise<void>;
};

export const useUpdateProcess = ({ checkForControllerUpdates }: IUpdateProcess) => {
  const { t, i18n } = useTranslation('installation');
  const { turnOnBackdropPopup, turnOffBackdropPopup } = useBackdropContext();

  const onUpdateControllerProgress = (
    updateProgress: UpdateProgressInfo,
    controllerUpdate?: IControllerAvailableUpdateInfo | null,
  ) => {
    turnOnBackdropPopup({
      header: t('exalus.controllerUpdate.updateProcessHeader'),
      message: (
        <p>
          {t('exalus.controllerUpdate.updateProcessInfo')}
          <span>{controllerUpdate?.NewResourceVersion || '-'}</span>
        </p>
      ),
      updateContent: {
        progress: updateProgress.Status === UpdateStatus.Upgraded ? 100 : updateProgress.PercentageTotal,
        status: updateProgress.Status,
        description: controllerUpdate?.UpdateDescription.get(langCode[i18n.language]),
        updateNumber: updateProgress.UpdateNumber,
        updatedResourceName: updateProgress.UpdatedResourceName.get(langCode[i18n.language]) || '',
        availableUpdates: [controllerUpdate?.UpdatedResourceName.get(langCode[i18n.language]) || ''],
        warning: `${t('exalus.controllerUpdate.updateProcessWarning')}`,
        handleClose: () => {
          turnOffBackdropPopup();
          checkForControllerUpdates();
        },
      },
      downloadContent: undefined,
    });
  };

  const onUpdateAllControllerProgresses = (
    updateProgress: UpdateProgressInfo,
    controllerUpdates?: IControllerAvailableUpdateInfo[] | null,
  ) => {
    if (!controllerUpdates) return;
    if (controllerUpdates?.length === 0) return;
    const controllerUpdate = controllerUpdates[updateProgress.UpdateNumber - 1];

    if (controllerUpdate) {
      turnOnBackdropPopup({
        header: t('exalus.controllerUpdate.updateProcessHeader'),
        message: t('exalus.controllerUpdate.updateProcessInfoShort'),
        updateContent: {
          progress: updateProgress.Status === UpdateStatus.Upgraded ? 100 : updateProgress.PercentageTotal,
          status: updateProgress.Status,
          description: controllerUpdate?.UpdateDescription.get(langCode[i18n.language]),
          updateNumber: updateProgress.UpdateNumber,
          updatedResourceName: updateProgress.UpdatedResourceName.get(langCode[i18n.language]) || '',
          availableUpdates: controllerUpdates.map(
            (update) => update.UpdatedResourceName.get(langCode[i18n.language]) as string,
          ),
          warning: `${t('exalus.controllerUpdate.updateProcessWarning')}`,
          handleClose: () => {
            turnOffBackdropPopup();
            checkForControllerUpdates();
          },
        },
        downloadContent: undefined,
      });
    }
  };

  const onDownloadControllerProgress = (downloadProgress: DownloadProgressInfo) => {
    turnOnBackdropPopup({
      header: t('exalus.controllerUpdate.updateProcessHeader'),
      message: t(`exalus.controllerUpdate.downloadProcessInfo.${downloadProgress.CurrentTask}`),
      downloadContent: {
        progress: downloadProgress.PercentageTotal,
        speedMbps: downloadProgress.SpeedMbps,
        updatedResourceName: downloadProgress.UpdatedResourceName.get(langCode[i18n.language]) || '',
      },
      updateContent: undefined,
    });
  };

  return {
    onUpdateControllerProgress,
    onUpdateAllControllerProgresses,
    onDownloadControllerProgress,
  };
};
